import type {Currency} from 'typings/portaro.be.types';
import {deepFreeze} from 'shared/utils/object-utils';

export type State =
    'NORMAL' |
    'EDITING' |
    'INITIAL' |
    'ERRORED' |
    'FINAL' |
    'QUESTION' |
    'PROCESSING' |
    'CHANGED';

type StateDefinition = {
    [name in State]: State;
};

export const states: StateDefinition = deepFreeze<StateDefinition>({
    NORMAL: 'NORMAL',
    EDITING: 'EDITING',
    INITIAL: 'INITIAL',
    FINAL: 'FINAL',
    QUESTION: 'QUESTION',
    PROCESSING: 'PROCESSING',
    ERRORED: 'ERRORED',
    CHANGED: 'CHANGED'
});

export const responseTypes = deepFreeze({
    EXCEPTION: 'exception',
    INTERRUPT: 'Interrupt',
    ACKNOWLEDGE: 'Acknowledge',
    FINISHED_RESPONSE: 'finishedResponse',
    PAYLOAD: 'Payload',
    REDIRECTION: 'redirectionResponse',
    PRINT_PAGE: 'printPage',
    RICH_TEXT_FINISHED_RESPONSE: 'richTextFinishedResponse',
} as const);

export const exceptionTypes = deepFreeze({
    FEATURE_NOT_ENABLED_EXCEPTION: 'FeatureNotEnabledException',
    ALREADY_EVALUATED_EXCEPTION: 'AlreadyEvaluatedException'
});

export const recordTypes = deepFreeze({
    TYPE_DOCUMENT: 'document',
    TYPE_AUTHORITY: 'authority'
});

export const actionRequestMethods = deepFreeze({
    GET: 'get',
    POST: 'post',
    DELETE: 'delete'
} as const);

export type ActionRequestMethod = typeof actionRequestMethods[keyof typeof actionRequestMethods];

export enum DetailLevel {
    ID = 'id',
    HEADER = 'header',
    FULL = 'full'
}

export enum SearchType {
    TYPE_CATALOG = 'catalog',
    TYPE_FIELDS = 'fields',
    TYPE_SEARCH_SELECTION = 'search-selection',
    TYPE_MOST_WATCHED = 'most-watched',
    TYPE_NEWEST = 'newest',
    TYPE_TOP_RATED = 'top-rated',
    TYPE_MOST_LENT = 'most-lent',
    TYPE_DOCUMENT_INDEX = 'document-index',
    TYPE_AUTHORITY_INDEX = 'authority-index',
    TYPE_EXEMPLAR_SEARCH = 'exemplar-search',
    TYPE_USER_SEARCH = 'user-search',
    TYPE_FILE_SEARCH = 'file-search',
    TYPE_RECORD_OPERATION_SEARCH = 'record-operation-search',
    TYPE_RECORD_REQUEST_SEARCH = 'record-request-search',
    TYPE_PAYMENT_SEARCH = 'payment-search',
    TYPE_INVENTORY_SEARCH = 'inventory-search',
    TYPE_CAPTURE_SEARCH = 'capture-search',
    TYPE_MATCH_SEARCH = 'match-search',
    TYPE_MESSAGE_SEARCH = 'message-search',
    TYPE_LOAN_SEARCH = 'loan-search',
    TYPE_GLOBAL = 'global',
    TYPE_FORM = 'complex',
    TYPE_BY_AUTHORITY = 'by-authority',
    TYPE_LOAN_REMINDER = 'loan-reminder-search',
    TYPE_MESSAGE_SENDING_SEARCH = 'message-sending-search',
    TYPE_PROVIDED_SEEKING_SEARCH = 'provided-seeking-search',
    TYPE_SEEKED_SEEKING_SEARCH = 'seeked-seeking-search'
}

export enum ViewFormat {
    STANDARD = 'standard',
    TABLE_ROW = 'table-row',
    // Sutor
    MONTH_ATTENDANCE_TABLE_ROW = 'month-attendance-table-row'
}

export enum UserType {
    PERSON_READER = 'person-reader',
    LIBRARIAN = 'librarian',
    LIBRARY = 'library',
    SUPPLIER = 'supplier',
    COMPANY = 'company',
    FAMILY = 'family',
    SOFTWARE = 'software'
}

export enum Kind {
    KIND_RECORD = 'record',
    KIND_EXEMPLAR = 'exemplar',
    KIND_USER = 'user',
    KIND_PAYMENT = 'payment',
    KIND_INVENTORY = 'inventory',
    KIND_CAPTURE = 'capture',
    KIND_MATCH = 'match',
    KIND_MESSAGE = 'message',
    KIND_FILE = 'file',
    KIND_RECORD_OPERATION = 'record-operation',
    KIND_RECORD_REQUEST = 'record-request',
    KIND_LOAN = 'loan',
    KIND_LOAN_REMINDER = 'loan-reminder',
    KIND_MESSAGE_SENDING = 'message-sending',
    KIND_SEEKING = 'seeking'
}

export enum Subkind {
    SUBKIND_DOCUMENT = 'document',
    SUBKIND_AUTHORITY = 'authority',
}

export enum DatasourceGroup {
    EXTERNAL = 'external-'
}

export const DEFAULT_CURRENCY = deepFreeze<Currency>({
    id: 'CZK',
    text: 'Kč'
});

export enum FileViewForm {
    UNKNOWN = 'UNKNOWN',
    DOCUMENT = 'DOCUMENT',
    IMAGE = 'IMAGE',
    THUMBNAIL = 'THUMBNAIL',
    PLAIN_TEXT = 'PLAIN_TEXT',
    RICH_DOCUMENT = 'RICH_DOCUMENT',
    PDF = 'PDF',
    MEDIA = 'MEDIA',
    AUDIO = 'AUDIO',
    VIDEO = 'VIDEO',
    SPREADSHEET = 'SPREADSHEET'
}

export const ExceptionSeverity = {
    SEVERITY_ERROR: 1,
    SEVERITY_WARNING: 2,
} as const;
